/** @format */

import React, {useContext, useEffect, useState} from 'react'

import {FullPageLoading} from '@src/components/Loading'

import ajax from '@src/ajax'

import {AllFormsContext} from './AllFormsContext'

export const FormContext = React.createContext({})

export const FormContextProvider = ({hashid, children}) => {
  const {forms, setForms, reloadAllForms} = useContext(AllFormsContext)

  const getCurrentForm = hashid => (forms || []).find(f => f.hashid === hashid)

  const [currentForm, setCurrentForm] = useState(getCurrentForm(hashid))

  const updateCurrentForm = form => {
    setCurrentForm(prev => ({...prev, ...form}))
    setForms(fms => {
      for (let i = 0; i < fms.length; i++) {
        if (fms[i].hashid === currentForm.hashid) {
          fms[i] = {...fms[i], ...form}
          break
        }
      }
      return [...fms]
    })
  }

  const reloadForm = async () => {
    if (!currentForm) {
      console.warn('No current form when calling reloadForm')
      return
    }
    await ajax({
      method: 'GET',
      endpoint: `/api-int/forms/${currentForm.hashid}`,
      onSuccess: form => {
        updateCurrentForm(form)
      },
      errorMsg: `Error loading form '${currentForm.hashid}'`,
    })
  }

  const deleteForm = async onSuccess => {
    await ajax({
      method: 'DELETE',
      endpoint: `/api-int/forms/${currentForm.hashid}`,
      errorMsg: 'Failed to delete form',
      successMsg: 'Form successfully deleted.',
      onSuccess: async () => {
        await reloadAllForms()
        onSuccess && onSuccess()
      },
    })
  }

  const loadSubmissions = async ({filter, limit, offset, query}) => {
    const params = {filter, limit, offset}
    if (query) {
      params.query = query
    }
    await ajax({
      method: 'GET',
      endpoint: `/api-int/forms/${currentForm.hashid}/submissions`,
      params,
      onSuccess: ({
        submissions,
        fields,
        total_pages,
        has_extra,
        spam_counter,
      }) => {
        setCurrentForm(prevForm => ({
          ...prevForm,
          submissions,
          fields,
          total_pages,
          has_extra,
          spam_counter,
        }))
      },
      errorMsg: 'Error fetching submissions',
    })
  }

  const patchForm = async (
    {value, attr},
    onSuccess,
    successMsg = 'Form updated.',
  ) => {
    await ajax({
      method: 'PATCH',
      endpoint: `/api-int/forms/${currentForm.hashid}`,
      payload: {
        [attr]: value,
      },
      errorMsg: 'Failed to save settings',
      successMsg,
      onSuccess: result => {
        updateCurrentForm(result)
        onSuccess && onSuccess()
      },
    })
  }

  const resetApiKey = async () => {
    await ajax({
      method: 'POST',
      endpoint: `/api-int/forms/${currentForm.hashid}/reset-apikey`,
      errorMsg: 'Failed to reset API key',
      successMsg: 'A new API key was generated and replaced the old one.',
      onSuccess: async () => reloadForm(),
    })
  }

  const patchSubmissions = async (payload, onSuccess) => {
    await ajax({
      method: 'PATCH',
      endpoint: `/api-int/forms/${currentForm.hashid}/submissions`,
      payload,
      onSuccess: result => {
        if (onSuccess) {
          onSuccess(result)
        }
      },
      successMsg: 'Submissions updated.',
      errorMsg: 'Failed to update submissions',
    })
  }

  const deleteSubmissions = async (selected, onSuccess) => {
    await ajax({
      method: 'DELETE',
      endpoint: `/api-int/forms/${currentForm.hashid}/submissions`,
      payload: {submissions: selected},
      onSuccess: result => {
        onSuccess && onSuccess(result)
      },
      successMsg: 'Submissions deleted.',
      errorMsg: 'Failed to delete submissions',
    })
  }

  const updateDisplayConfig = async config => {
    patchForm({attr: 'display_config', value: config}, null, null)
  }

  useEffect(() => {
    const form = getCurrentForm(hashid)
    setCurrentForm(form ? {...form} : undefined)
  }, [hashid])

  if (!forms || !currentForm) {
    return <FullPageLoading />
  }

  return (
    <FormContext.Provider
      value={{
        currentForm,
        updateCurrentForm,
        reloadForm,
        patchForm,
        deleteForm,
        resetApiKey,
        loadSubmissions,
        patchSubmissions,
        deleteSubmissions,
        updateDisplayConfig,
      }}
    >
      {children}
    </FormContext.Provider>
  )
}
